import React, { useCallback, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TouchSpinProps {
  value: number;
  onChange: (_value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  label?: string;
  id?: string;
}

const TouchSpin: React.FC<TouchSpinProps> = ({
  value,
  onChange,
  min = 1,
  max = 9999,
  step = 1,
  disabled = false,
  className = '',
  inputClassName = '',
  label,
  id,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const inputId = id || `touchspin-${generatedId}`;

  const handleIncrement = useCallback(() => {
    const newValue = Math.min(value + step, max);
    if (newValue !== value) {
      onChange(newValue);
    }
  }, [value, step, max, onChange]);

  const handleDecrement = useCallback(() => {
    const newValue = Math.max(value - step, min);
    if (newValue !== value) {
      onChange(newValue);
    }
  }, [value, step, min, onChange]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = parseInt(e.target.value);
      if (isNaN(inputValue)) {
        onChange(min);
        return;
      }
      const clampedValue = Math.max(min, Math.min(max, inputValue));
      onChange(clampedValue);
    },
    [min, max, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleIncrement();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleDecrement();
      }
    },
    [handleIncrement, handleDecrement]
  );

  useEffect(() => {
    if (value < min) {
      onChange(min);
    } else if (value > max) {
      onChange(max);
    }
  }, [value, min, max, onChange]);

  const isDecrementDisabled = disabled || value <= min;
  const isIncrementDisabled = disabled || value >= max;

  return (
    <div className={`touchspin d-flex align-items-center ${className}`}>
      {label && (
        <label htmlFor={inputId} className="form-label mb-0 me-2 sr-only">
          {label}
        </label>
      )}
      <button
        className="touchspin__button decrease"
        onClick={handleDecrement}
        disabled={isDecrementDisabled}
        aria-label={t('components.touchspin.decrease')}
      >
        <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.1875 10H11.8125"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <input
        ref={inputRef}
        id={inputId}
        type="number"
        className={`form-control text-center ${inputClassName} p-0 fw-medium`}
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={label || t('components.touchspin.quantity')}
      />
      <button
        className="touchspin__button increase"
        onClick={handleIncrement}
        disabled={isIncrementDisabled}
        aria-label={t('components.touchspin.increase')}
      >
        <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.1875 10H11.8125"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 5.1875V14.8125"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default TouchSpin;
